import { useMutation, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { toast } from "../utils/notifications";
import { createAdminRow, deleteAdminRow, updateAdminRow } from "../services/adminCrud";

type CrudAction = "create" | "update" | "delete";

interface ToastText {
  title: string;
  /** Omitted from the toast entirely when not given. */
  description?: string;
}

type ActionMessages = { success?: ToastText; error?: string };

interface AdminCrudOptions {
  /** Collection endpoint: POST `endpoint`, PUT/DELETE `endpoint/:id`. */
  endpoint: string;
  /** Lower-case noun for the default messages, e.g. "ink price". */
  label: string;
  /** Invalidated after every successful create, update and delete. */
  invalidate: QueryKey;
  /** Further keys invalidated after a successful create, in order. */
  createAlsoInvalidates?: QueryKey[];
  /** Overrides the default per-action toasts. */
  messages?: Partial<Record<CrudAction, ActionMessages>>;
  /** Runs after a successful create or update (after the invalidations). */
  onSaved?: () => void;
  /** Runs after a successful delete (after the invalidation). */
  onDeleted?: () => void;
}

const PAST: Record<CrudAction, string> = { create: "created", update: "updated", delete: "deleted" };

function defaultMessages(label: string, action: CrudAction): Required<ActionMessages> {
  const noun = label.charAt(0).toUpperCase() + label.slice(1);
  return {
    success: { title: "Success", description: `${noun} ${PAST[action]} successfully` },
    error: `Failed to ${action} ${label}`,
  };
}

function serverError(error: unknown): string | undefined {
  return (error as { response?: { data?: { error?: string } } } | null)?.response?.data?.error;
}

/**
 * The create / update / delete mutations of an admin price-table page
 * (Generic items, Ink prices, Curated prices). Each success shows a toast,
 * invalidates the page's queries, then runs the page's callback; each failure
 * shows the server's error, or a fallback.
 */
export function useAdminCrud<TData = unknown>({
  endpoint,
  label,
  invalidate,
  createAlsoInvalidates = [],
  messages,
  onSaved,
  onDeleted,
}: AdminCrudOptions) {
  const queryClient = useQueryClient();

  const text = (action: CrudAction): Required<ActionMessages> => {
    const defaults = defaultMessages(label, action);
    return {
      success: messages?.[action]?.success ?? defaults.success,
      error: messages?.[action]?.error ?? defaults.error,
    };
  };

  const succeed = (action: CrudAction, alsoInvalidate: QueryKey[], then?: () => void) => {
    const { title, description } = text(action).success;
    toast(description === undefined ? { title, severity: "success" } : { title, description, severity: "success" });
    for (const queryKey of [invalidate, ...alsoInvalidate]) {
      void queryClient.invalidateQueries({ queryKey });
    }
    then?.();
  };

  const fail = (action: CrudAction) => (error: unknown) => {
    toast({ title: "Error", description: serverError(error) || text(action).error, severity: "error" });
  };

  const createMutation = useMutation({
    mutationFn: (data: TData) => createAdminRow(endpoint, data),
    onSuccess: () => succeed("create", createAlsoInvalidates, onSaved),
    onError: fail("create"),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: TData }) => updateAdminRow(endpoint, id, data),
    onSuccess: () => succeed("update", [], onSaved),
    onError: fail("update"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteAdminRow(endpoint, id),
    onSuccess: () => succeed("delete", [], onDeleted),
    onError: fail("delete"),
  });

  return { createMutation, updateMutation, deleteMutation };
}
